import { Injectable }                      from '@angular/core';
import {
	FISKAL_CONTRACT_METADATA,
	IFiskalContractMetadata
}                                          from '@app/modules/contract/config/fiskal-contract.config';
import {
	ContractPresetEnum,
	FiskalContract,
	FiskalCurrentTenantContract
}                                          from '@app/modules/contract/models/contract.model';
import {
	ContractDetailPageRoute,
	ContractDetailPageTabRoute,
	ContractFormPageCreateRoute,
	ContractFormPageUpdateRoute,
	ContractListPageProductRoute,
	ContractRoutesEnum,
	ContractUsagePageRoute
}                                          from '@app/modules/contract/routes/contract.routes';
import { IPropsUpdateFiskalContractState } from '@app/modules/contract/store/fiskal-contract-store/fiskal-contract.actions';
import { fiskalContractStore }             from '@app/modules/contract/store/fiskal-contract-store/fiskal-contract.store';
import {
	MyAccountProductContractDetailRoute,
	MyAccountProductContractDetailTabRoute,
	MyAccountProductContractFormPageUpdateRoute,
	MyAccountProductContractsRoute,
	MyAccountProductContractUsageRoute,
	MyAccountProductViewContractDetailRoute,
	MyAccountProductViewContractDetailTabRoute,
	MyAccountRoutesEnum
}                                          from '@app/modules/my-account/routes/my-account.routes';
import { getYearMonth }                    from '@app/modules/usage/helpers/usage-month.helper';
import { AbstractBaseService }             from '@core/bases/services/abstractBaseService';
import {
	IContractHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                          from '@core/interfaces/api.interface';
import {
	AllowedDetailsTabEnum,
	ProductEnum
}                                          from '@core/interfaces/common.interfaces';
import {
	IRouteDefinition,
	RouteBasePath
}                                          from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                          from '@core/interfaces/store.interface';
import { FiskalContractApiService }        from '@http/contracts/fiskal-contract-api.service';
import { PresetNavigateBackConfig }        from '@models/preset';
import { Store }                           from '@ngrx/store';
import { ListOperationsIdentifierEnum }    from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                    from '@shared/services/toaster.service';
import { Observable }                      from 'rxjs';
import { take }                            from 'rxjs/operators';

@Injectable( { providedIn: 'root' } )
export class FiskalContractService extends AbstractBaseService {

	private readonly product = ProductEnum.FISCAL;

	readonly fiskalContractMetadata: IFiskalContractMetadata = FISKAL_CONTRACT_METADATA;

	constructor(
		private readonly fiskalContractApiService: FiskalContractApiService,
		protected readonly store$: Store
	) {
		super( store$, fiskalContractStore.selectors, fiskalContractStore.actions );
		this.logger.trace( 'FiskalContractService constructor' );
	}

	// ACTIONS ###########################################################################
	private deleteContract( contractId: string, navigateToList: boolean, presetId: ContractPresetEnum, caller: string ) {
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			FISKAL_CONTRACT_METADATA.i18n.basepath,
			'entity.deleteDialog.description'
		);
		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText, 'delete' )
		.pipe( take( 1 ) )
		.subscribe( res => {
			this.uiService.showPageLoaderAndCountUp();
			this.delete( contractId )
			.pipe( take( 1 ) )
			.subscribe(
				( contractState: RequestCompleteEntityState<FiskalContract> ) => {
					if ( contractState.requestState === RequestState.SUCCESS ) {
						this.storeRemoveOne<FiskalContract>( contractId );
					} else if ( contractState.requestState === RequestState.FAILED ) {
						this.toasterService.dispatch(
							contractState,
							this.fiskalContractMetadata.entityId,
							actionStatus.DELETE
						);
					}
					this.uiService.hidePageLoaderOrCountDown();
					if ( navigateToList ) { this.navigateToListPage( presetId ); }
				},
				error => {
					this.uiService.hidePageLoaderOrCountDown();
					this.toasterService.dispatch(
						error,
						this.fiskalContractMetadata.entityId,
						actionStatus.DELETE
					);
				}
			);
		} );
	}

	deleteContractOnDetailPage( contractId: string, presetId: ContractPresetEnum, caller: string ) {
		this.deleteContract( contractId, true, presetId, caller );
	}

	deleteContractOnEntity( contractId: string, presetId: ContractPresetEnum, caller: string ) {
		this.deleteContract( contractId, true, presetId, caller );
	}


	private pageReadContractHandler(
		readReq$: Observable<RequestCompleteEntityState<FiskalContract>>,
		presetId: ContractPresetEnum
	): void {
		readReq$
		.pipe( take( 1 ) )
		.subscribe( ( contractState: RequestCompleteEntityState<FiskalContract> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			// on fail redirects to list
			if ( contractState.requestState === RequestState.FAILED ) {
				this.toasterService.dispatch( contractState, this.fiskalContractMetadata.entityId, actionStatus.READ );
				this.navigateToListPage( presetId );
			}
		} );
	}

	readContractOnDetailsPage( contractId: string, presetId: ContractPresetEnum ): void {
		this.uiService.showPageLoaderAndCountUp();

		const readReq$: Observable<RequestCompleteEntityState<FiskalContract>> =
			this.read( presetId, contractId, false, null );

		this.pageReadContractHandler( readReq$, presetId );
	}

	readContractOnFormPage( contractId: string, presetId: ContractPresetEnum ): void {
		this.uiService.showPageLoaderAndCountUp();

		const readReq$: Observable<RequestCompleteEntityState<FiskalContract>> =
			this.read( presetId, contractId, true, null );

		this.pageReadContractHandler( readReq$, presetId );
	}

	// STORE ###########################################################################
	// SELECTORS
	// UPDATERS
	updateStoreState( updateContractState: IPropsUpdateFiskalContractState ): void {
		this.store$.dispatch( fiskalContractStore.actions.updateContractState( updateContractState ) );
	}

	// HELPER #################################################################################

	// NAVIGATION #############################################################################
	navigateToListPage( presetId: ContractPresetEnum ): void {
		let routeDefinition: IRouteDefinition;

		const defaultContractListPageProductRouteDefinition: ContractListPageProductRoute = {
			basePath: RouteBasePath.Contracts,
			path: ContractRoutesEnum.ListPageProduct,
			params: { product: this.product },
			replaceLastStoredRouteWithNew: true
		};

		switch ( presetId ) {
			case ContractPresetEnum.CONTRACT_FISKAL:
				routeDefinition = defaultContractListPageProductRouteDefinition;
				break;

			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
				const myAccountProductContractsRouteDefinition: MyAccountProductContractsRoute = {
					basePath: RouteBasePath.MyAccount,
					path: MyAccountRoutesEnum.PageProductContract,
					params: {
						product: this.product
					},
					replaceLastStoredRouteWithNew: true
				};
				routeDefinition = myAccountProductContractsRouteDefinition;
				break;

			default:
				routeDefinition = defaultContractListPageProductRouteDefinition;
		}

		this.routerService.hasRouteByIdTheSameBasePath( '0', routeDefinition?.basePath )
		.pipe( take( 1 ) )
		.subscribe( ( hasSameBasePath: boolean ) => {
			this.routerService.navigateToNextRoute(
				routeDefinition,
				'FiskalContractService:navigateToListPage'
			);
		} );
	}

	navigateToDetailPage( contractId: string, presetId: ContractPresetEnum ): void {
		if ( contractId ) {
			let routeDefinition: IRouteDefinition;

			const defaultContractDetailPageRouteDefinition: ContractDetailPageRoute = {
				basePath: RouteBasePath.Contracts,
				path: ContractRoutesEnum.DetailPage,
				params: { contractId, product: this.product }
			};

			const defaultMyAccountProductContractDetailRouteDefinition: MyAccountProductContractDetailRoute = {
				basePath: RouteBasePath.MyAccount,
				path: MyAccountRoutesEnum.PageProductContractDetail,
				params: {
					contractId,
					product: this.product
				}
			};

			switch ( presetId ) {
				case ContractPresetEnum.CONTRACT_FISKAL:
					routeDefinition = defaultContractDetailPageRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
					routeDefinition = defaultMyAccountProductContractDetailRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
					const myAccountProductViewContractDetailRouteDefinition: MyAccountProductViewContractDetailRoute = {
						...defaultMyAccountProductContractDetailRouteDefinition,
						path: MyAccountRoutesEnum.PageProductViewContractDetail,
						params: {
							contractId,
							product: this.product
						}
					};

					routeDefinition = myAccountProductViewContractDetailRouteDefinition;
					break;

				default:
					routeDefinition = defaultContractDetailPageRouteDefinition;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'FiskalContractService:navigateToDetailPage'
			);
		}
	}

	navigateToDetailTab( contractId: string, tab: AllowedDetailsTabEnum, presetId: ContractPresetEnum ): void {
		if ( contractId ) {
			let routeDefinition: IRouteDefinition;

			const defaultContractDetailPageTabRouteDefinition: ContractDetailPageTabRoute = {
				basePath: RouteBasePath.Contracts,
				path: ContractRoutesEnum.DetailPageTab,
				params: {
					contractId,
					product: this.product,
					tab
				},
				replaceLastStoredRouteWithNew: true
			};

			const defaultMyAccountProductContractDetailTabRouteDefinition: MyAccountProductContractDetailTabRoute = {
				basePath: RouteBasePath.MyAccount,
				path: MyAccountRoutesEnum.PageProductContractDetailTab,
				params: {
					contractId,
					product: this.product,
					tab
				},
				replaceLastStoredRouteWithNew: true
			};

			switch ( presetId ) {
				case ContractPresetEnum.CONTRACT_FISKAL:
					routeDefinition = defaultContractDetailPageTabRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
					routeDefinition = defaultMyAccountProductContractDetailTabRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
					const myAccountProductViewContractDetailTabRouteDefinition: MyAccountProductViewContractDetailTabRoute = {
						...defaultMyAccountProductContractDetailTabRouteDefinition,
						path: MyAccountRoutesEnum.PageProductViewContractDetailTab,
						params: {
							contractId,
							product: this.product,
							tab
						},
						replaceLastStoredRouteWithNew: true
					};

					routeDefinition = myAccountProductViewContractDetailTabRouteDefinition;
					break;

				default:
					routeDefinition = defaultContractDetailPageTabRouteDefinition;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'FiskalContractService:navigateToDetailTab'
			);
		}
	}

	navigateToFormCreate(): void {
		const routeDefinition: ContractFormPageCreateRoute = {
			basePath: RouteBasePath.Contracts,
			path: ContractRoutesEnum.FormPageCreate,
			params: { product: this.product }
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'FiskalContractService:navigateToCreate'
		);
	}

	navigateToFormUpdate( contractId: string, presetId: ContractPresetEnum ): void {
		if ( contractId ) {
			let routeDefinition: IRouteDefinition;

			const defaultContractFormPageUpdateRouteDefinition: ContractFormPageUpdateRoute = {
				basePath: RouteBasePath.Contracts,
				path: ContractRoutesEnum.FormPageUpdate,
				params: { contractId, product: this.product }
			};

			switch ( presetId ) {
				case ContractPresetEnum.CONTRACT_FISKAL:
					routeDefinition = defaultContractFormPageUpdateRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
					const myAccountProductContractFormPageUpdateRouteDefinition: MyAccountProductContractFormPageUpdateRoute = {
						basePath: RouteBasePath.MyAccount,
						path: MyAccountRoutesEnum.PageProductContractFormPageUpdate,
						params: {
							contractId,
							product: this.product
						}
					};
					routeDefinition = myAccountProductContractFormPageUpdateRouteDefinition;
					break;

				default:
					routeDefinition = defaultContractFormPageUpdateRouteDefinition;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'FiskalContractService:navigateToUpdate'
			);
		}
	}

	navigateToUsage( contractId: string, presetId: ContractPresetEnum ): void {
		if ( contractId ) {
			let routeDefinition: IRouteDefinition;

			const defaultContractUsagePageRouteDefinition: ContractUsagePageRoute = {
				basePath: RouteBasePath.Contracts,
				path: ContractRoutesEnum.UsagePage,
				params: {
					product: this.product,
					contractId,
					usagePeriod: getYearMonth()
				}
			};

			switch ( presetId ) {
				case ContractPresetEnum.CONTRACT_FISKAL:
					routeDefinition = defaultContractUsagePageRouteDefinition;
					break;

				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
				case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
					const myAccountProductContractUsageRouteDefinition: MyAccountProductContractUsageRoute = {
						basePath: RouteBasePath.MyAccount,
						path: MyAccountRoutesEnum.PageProductContractUsage,
						params: {
							product: this.product,
							contractId,
							usagePeriod: getYearMonth()
						}
					};
					routeDefinition = myAccountProductContractUsageRouteDefinition;
					break;

				default:
					routeDefinition = defaultContractUsagePageRouteDefinition;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'FiskalContractService:navigateToUsage',
				false,
				false,
				true
			);
		}
	}

	navigateBack(
		options: {
			navigateBackFallback: PresetNavigateBackConfig,
			caller: string,
			shouldRestoreListOperationData: boolean
		}
	): void {

		const fallbackRouteDefinition =
			this.routerService.getFallbackRoute(
				options?.navigateBackFallback?.fallbackRoute.basePath,
				options?.navigateBackFallback?.fallbackRoute.path,
				{ product: ProductEnum.FISCAL }
			);

		this.listOperationsService.state.listOperationsSetRestoreFlag(
			options?.navigateBackFallback?.listOperationsId,
			options?.shouldRestoreListOperationData,
			options?.caller
		);

		this.routerService.navigateBack(
			fallbackRouteDefinition,
			options?.caller + ' | FiskalContractService:navigateBack'
		);
	}

	// API #################################################################################
	// List Requests
	searchWithStoredListOperationsData(
		presetId: ContractPresetEnum,
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<RequestCompleteListState<FiskalContract>> {
		switch ( presetId ) {
			case ContractPresetEnum.CONTRACT_FISKAL:
				return this._searchWithStoredListOperationsData( listOperationsId, presetId, caller );

			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
				return this._searchWithStoredListOperationsData( listOperationsId, presetId, caller );
		}
	}

	private _searchWithStoredListOperationsData(
		listOperationsId: ListOperationsIdentifierEnum,
		presetId: ContractPresetEnum,
		caller: string
	): Observable<RequestCompleteListState<FiskalContract>> {

		return this.listOperationsService.helper.requestWithStoredListOperationsData<FiskalContract, IContractHttpParams>(
			( [ presetIdArg, listOperationsArg, callerArg ] ) =>
				this.search( presetIdArg, listOperationsArg, callerArg ),
			[ presetId, `${ caller } | FiskalContractService:searchWithStoredListOperationsData`, null ],
			1,
			listOperationsId
		);
	}

	search(
		presetId: ContractPresetEnum,
		params?: IContractHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig,
		getAllPagesData?: boolean
	): Observable<RequestCompleteListState<FiskalContract>> {
		switch ( presetId ) {
			case ContractPresetEnum.CONTRACT_FISKAL:
				return this._search( params, true, caller, storeConfig, getAllPagesData );

			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
				return this._search( params, false, caller, storeConfig, getAllPagesData );
		}
	}

	private _search(
		params?: IContractHttpParams,
		useAdminApi?: boolean,
		caller?: string,
		storeConfig?: IStoreConfig,
		getAllPagesData?: boolean
	): Observable<RequestCompleteListState<FiskalContract>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<FiskalContract>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<FiskalContract>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ContractService:search` };

		params = this.generateParams( params );

		const getAllPagesReq$ = ( [ paramsArg ] ) => this.fiskalContractApiService.search( paramsArg, useAdminApi );
		const req$ = getAllPagesData
								 ? this.requestAllPagesData<FiskalContract>( getAllPagesReq$, [ params ], 0 )
								 : this.fiskalContractApiService.search( params, useAdminApi );

		return this.requestListThroughProxy<FiskalContract>( req$, storeProcessingConfig );
	}


	// Entity Requests
	read(
		presetId: ContractPresetEnum,
		id: string,
		withFullResponse?: boolean,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		switch ( presetId ) {
			case ContractPresetEnum.CONTRACT_FISKAL:
				return this._read( id, true, withFullResponse, storeConfig );

			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
				return this._read( id, false, withFullResponse, storeConfig );
		}
	}

	// TODO to be deprecating, at the moment we need to keep for the form, use read method everywhere else
	readCustomer(
		id: string,
		withFullResponse?: boolean,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		return this._read( id, false, withFullResponse, storeConfig );
	}

	// TODO to be deprecating, at the moment we need to keep for the form, use read method everywhere else
	readAdmin(
		id: string,
		withFullResponse?: boolean,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		return this._read( id, true, withFullResponse, storeConfig );
	}

	private _read(
		id: string,
		useAdminApi: boolean,
		withFullResponse?: boolean,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		// request from store must be skipped when withFullResponse is set true
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<FiskalContract>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$<FiskalContract>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this.fiskalContractApiService.read( id, useAdminApi, withFullResponse );
		return this.requestEntityThroughProxy<FiskalContract>( id, req$, storeProcessingConfig );
	}


	create(
		contractForm: FiskalContract,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<FiskalContract>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this.fiskalContractApiService.create( contractForm );
		return this.requestEntityThroughProxy<FiskalContract>( null, req$, storeProcessingConfig );
	}

	update(
		presetId: ContractPresetEnum,
		contractForm: FiskalContract,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		switch ( presetId ) {
			case ContractPresetEnum.CONTRACT_FISKAL:
				return this._update( contractForm, true, storeConfig );

			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL:
			case ContractPresetEnum.MY_ACCOUNT_CONTRACT_FISKAL_DETAIL_VIEW:
				return this._update( contractForm, false, storeConfig );
		}
	}

	// TODO to be deprecating, at the moment we need to keep for the form, use update method everywhere else
	updateAdmin(
		contractForm: FiskalContract,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		return this._update( contractForm, true, storeConfig );
	}

	// TODO to be deprecating, at the moment we need to keep for the form, use update method everywhere else
	updateCustomer(
		contractForm: FiskalContract,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		return this._update( contractForm, false, storeConfig );
	}

	private _update(
		contractForm: FiskalContract,
		useAdminApi: boolean,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteEntityState<FiskalContract>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<FiskalContract>(
			storeConfig,
			this.storeEntityById$( contractForm?.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this.fiskalContractApiService.update( contractForm, useAdminApi );
		return this.requestEntityThroughProxy( contractForm?.id, req$, storeProcessingConfig );
	}

	private delete( contractId: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<FiskalContract>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<FiskalContract>(
			storeConfig ?? defaultStoreConfig,
			this.storeEntityById$( contractId ),
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this.fiskalContractApiService.delete( contractId );
		return this.requestEntityThroughProxy<FiskalContract>( contractId, req$, storeProcessingConfig );
	}

	public currentTenantContract( throwError = false ): Observable<RequestCompleteEntityState<FiskalCurrentTenantContract>> {
		const req$ = this.fiskalContractApiService.currentTenantContract();
		return this.requestEntityThroughProxy( null, req$ );
	}
}
